import { contactPreferredAddress } from "@shared/contactEmailSelection";
import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { X, Trash2, Phone, Video, Users, User, Building2, Target, Link2, Calendar, Mail, CalendarRange, UserPlus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceAssistantTextarea } from "@/components/ui/VoiceAssistantTextarea";
import { Label } from "@/components/ui/label";
import { Autocomplete, AutocompleteOption } from "@/components/ui/autocomplete";
import { apiRequest } from "@/lib/queryClient";
import { entityDetailKey, invalidateMeetingViews } from "@/lib/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { ToastAction } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { CrmRecordAutocomplete } from "@/components/ui/crm-record-autocomplete";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { MartinaOutputRating } from "@/components/exai/MartinaOutputRating";
import { isMeetingParentEventCandidate, meetingEventOverlapsLocalDate } from "@shared/meetingParentEvent";

interface SimpleMeetingModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  defaultDate?: Date;
  onMeetingCreated?: (startDate: Date, meetingId: number, meeting: any) => void;
  companyId?: number;
  branchId?: number;
  contactId?: number;
  dealId?: number;
  leadId?: number;
}

const MEETING_TYPES = [
  { value: "Call", label: "Chiamata", icon: Phone },
  { value: "Virtual", label: "Virtuale", icon: Video },
  { value: "In-Person", label: "Di persona", icon: Users },
  { value: "Evento", label: "Evento", icon: CalendarRange },
];

type MeetingParticipantDraft = {
  contactId: number | null;
  companyId: number | null;
  companyName?: string | null;
  email: string | null;
  displayName: string | null;
  role: string;
  isOptional: boolean;
  invitedAt?: string | null;
  inviteChannel?: "crm" | "google" | null;
};

type InviteDeliveryIntent = "none" | "all";

type PendingInvitePreview = {
  meetingId: number;
  channel: "crm" | "google";
  recipients: string[];
  removedRecipients: string[];
  subject: string;
  body: string;
  from: string | null;
  blockedReason: string | null;
  previewToken: string;
};

function legacyMeetingParticipantDrafts(attendees: any): MeetingParticipantDraft[] {
  const external = Array.isArray(attendees?.external) ? attendees.external : [];
  const seen = new Set<string>();
  return external.flatMap((participant: any) => {
    if (!participant || typeof participant !== "object") return [];
    const email = typeof participant.email === "string" ? participant.email.trim() : "";
    const contactId = Number.isSafeInteger(Number(participant.contactId)) && Number(participant.contactId) > 0
      ? Number(participant.contactId)
      : null;
    const key = contactId ? `contact:${contactId}` : email ? `email:${email.toLowerCase()}` : "";
    if (!key || seen.has(key)) return [];
    seen.add(key);
    return [{
      contactId,
      companyId: participant.companyId ? Number(participant.companyId) : null,
      companyName: participant.companyName || null,
      email: email || null,
      displayName: participant.name || participant.displayName || null,
      role: "attendee",
      isOptional: participant.isOptional === true,
    }];
  });
}

export function SimpleMeetingModal({
  isOpen,
  onOpenChange,
  initialData,
  defaultDate,
  onMeetingCreated,
  companyId: propCompanyId,
  branchId: propBranchId,
  contactId: propContactId,
  dealId: propDealId,
  leadId: propLeadId,
}: SimpleMeetingModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [meetingType, setMeetingType] = useState("Call");
  const [calendarScope, setCalendarScope] = useState<"work" | "private">("work");
  const [contactId, setContactId] = useState<number | null>(propContactId ?? null);
  const [companyId, setCompanyId] = useState<number | null>(propCompanyId ?? null);
  const [branchId, setBranchId] = useState<number | null>(propBranchId ?? null);
  const [dealId, setDealId] = useState<number | null>(propDealId ?? null);
  const [leadId, setLeadId] = useState<number | null>(propLeadId ?? null);
  const [synergyId, setSynergyId] = useState<number | null>(null);
  const [participants, setParticipants] = useState<MeetingParticipantDraft[]>([]);
  const [participantPickerValue, setParticipantPickerValue] = useState("");
  const [externalParticipantEmail, setExternalParticipantEmail] = useState("");
  const [date, setDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [eventHasTimes, setEventHasTimes] = useState(false);
  const [eventId, setEventId] = useState<number | null>(null);
  const [eventInheritedDealId, setEventInheritedDealId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [syncWithGoogle, setSyncWithGoogle] = useState(true);
  const [inviteDeliveryIntent, setInviteDeliveryIntent] = useState<InviteDeliveryIntent>("none");
  const [pendingInvitePreview, setPendingInvitePreview] = useState<PendingInvitePreview | null>(null);
  // This form has local state rather than react-hook-form. Only user edits
  // activate the shared close/navigation guard; async defaults do not.
  const [userEdited, setUserEdited] = useState(false);
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(isOpen, userEdited, onOpenChange);
  const meetingMartinaContext = initialData?.id
    ? { terminalType: "entity" as const, entityType: "meeting", entityId: String(initialData.id), route: `/meetings/${initialData.id}`, parentEntityType: contactId ? "contact" : companyId ? "company" : dealId ? "deal" : undefined, parentEntityId: contactId ? String(contactId) : companyId ? String(companyId) : dealId ? String(dealId) : undefined }
    : contactId ? { terminalType: "entity" as const, entityType: "contact", entityId: String(contactId), route: `/contacts/${contactId}` } : companyId ? { terminalType: "entity" as const, entityType: "company", entityId: String(companyId), route: `/companies/${companyId}` } : dealId ? { terminalType: "entity" as const, entityType: "deal", entityId: String(dealId), route: `/deals/${dealId}` } : { terminalType: "calendar" as const, route: "/calendar" };

  // Fetch sync status per default checkbox
  const { data: syncStatus } = useQuery<any>({
    queryKey: ['/api/google-calendar/sync-status'],
    enabled: isOpen,
    retry: 1,
  });
  const { data: inviteChannel } = useQuery<{ channel: "crm" | "google" }>({
    queryKey: ['/api/google-calendar/invite-channel'],
    enabled: isOpen,
    retry: 1,
  });

  useEffect(() => {
    if (isOpen && syncStatus) {
      setSyncWithGoogle(!!syncStatus.syncEnabled && !!syncStatus.connected);
    }
  }, [isOpen, syncStatus]);

  // Fetch all contacts + company-linked contacts for priority grouping
  const { data: rawContacts } = useQuery<any>({ queryKey: ['/api/contacts'], enabled: isOpen });
  const { data: linkedContacts } = useQuery<any>({
    queryKey: [`/api/companies/${companyId}/contacts`],
    queryFn: async () => {
      if (!companyId) return [];
      const data = await apiRequest(`/api/companies/${companyId}/contacts`);
      return data ?? [];
    },
    enabled: isOpen && !!companyId,
  });
  const { data: rawCompanies } = useQuery<any>({ queryKey: ['/api/companies'], enabled: isOpen });
  const { data: rawDeals } = useQuery<any>({ queryKey: ['/api/deals'], enabled: isOpen });
  // La lista globale e' paginata. Quando il meeting e' legato a un Deal, la
  // relazione deve arrivare dal reader canonico owner-scoped del Deal: una
  // Sinergia oltre la prima pagina globale non puo' sparire dal form.
  const { data: rawSynergies } = useQuery<any>({
    queryKey: ['/api/synergies'],
    enabled: isOpen && !dealId,
  });
  const { data: rawDealSynergies } = useQuery<any>({
    queryKey: [`/api/synergies/deal/${dealId ?? 'pending'}`],
    queryFn: () => {
      if (dealId == null) throw new Error('DEAL_ID_REQUIRED');
      return apiRequest(`/api/synergies/deal/${dealId}`);
    },
    enabled: isOpen && dealId != null,
    retry: false,
    staleTime: 60_000,
  });
  const { data: selectedContactDetail } = useQuery<any>({
    queryKey: ['/api/contacts', contactId],
    queryFn: () => apiRequest(`/api/contacts/${contactId}`),
    enabled: isOpen && !!contactId,
    retry: false,
    staleTime: 60_000,
  });
  const { data: selectedDealDetail } = useQuery<any>({
    queryKey: entityDetailKey('deals', dealId ?? 'pending'),
    queryFn: () => {
      if (dealId == null) throw new Error('DEAL_ID_REQUIRED');
      return apiRequest(`/api/deals/${dealId}`);
    },
    enabled: isOpen && !!dealId,
    retry: false,
    staleTime: 60_000,
  });
  const { data: selectedLeadDetail } = useQuery<any>({
    queryKey: entityDetailKey('leads', leadId ?? 'pending'),
    queryFn: () => {
      if (leadId == null) throw new Error('LEAD_ID_REQUIRED');
      return apiRequest(`/api/leads/${leadId}`);
    },
    enabled: isOpen && !!leadId,
    retry: false,
    staleTime: 60_000,
  });

  // Un solo reader canonico per scegliere o correggere l’Evento padre.
  // La compatibilità di data ordina i suggerimenti senza limitare la scelta.
  const { data: rawEvents, isLoading: loadingEvents, isError: eventsError } = useQuery<any>({
    queryKey: ['/api/meetings', { meetingType: 'Evento' }],
    queryFn: () => apiRequest('/api/meetings?meetingType=Evento'),
    enabled: isOpen && meetingType !== 'Evento',
    staleTime: 60_000,
  });

  const contacts: any[] = Array.isArray(rawContacts) ? rawContacts : (rawContacts?.data ?? []);
  const companyContacts: any[] = Array.isArray(linkedContacts) ? linkedContacts : [];
  const companies: any[] = Array.isArray(rawCompanies) ? rawCompanies : (rawCompanies?.data ?? []);
  const deals: any[] = Array.isArray(rawDeals) ? rawDeals : (rawDeals?.data ?? []);
  const selectedSynergySource = dealId != null ? rawDealSynergies : rawSynergies;
  const allSynergies: any[] = Array.isArray(selectedSynergySource) ? selectedSynergySource : (selectedSynergySource?.data ?? []);

  const contactById = (id: number | null) => [...contacts, ...companyContacts].find((contact: any) => Number(contact.id) === Number(id));
  const normalizeContactRecord = (payload: any) => {
    const contact = payload?.data ?? payload?.contact ?? payload;
    if (!contact || typeof contact !== "object") return null;
    return {
      ...contact,
      id: Number(contact.id),
      companyId: contact.companyId != null ? Number(contact.companyId) : contact.company_id != null ? Number(contact.company_id) : null,
      companyName: contact.companyName ?? contact.company_name ?? contact.company?.name ?? null,
      companyEmail: contact.companyEmail ?? contact.company_email ?? null,
      privateEmail: contact.privateEmail ?? contact.private_email ?? null,
      firstName: contact.firstName ?? contact.first_name ?? null,
      lastName: contact.lastName ?? contact.last_name ?? null,
    };
  };
  const makeContactParticipant = (id: number, role = "attendee", suppliedContact?: any): MeetingParticipantDraft | null => {
    const contact = normalizeContactRecord(suppliedContact) ?? normalizeContactRecord(contactById(id));
    if (!contact) return null;
    return {
      contactId: id,
      companyId: contact.companyId ? Number(contact.companyId) : null,
      companyName: contact.companyName || null,
      email: contactPreferredAddress(contact) || null,
      displayName: [contact.firstName, contact.lastName].filter(Boolean).join(" ") || null,
      role,
      isOptional: false,
    };
  };

  useEffect(() => {
    if (contactId && !companyId) {
      const contact = contacts.find((item: any) => Number(item.id) === Number(contactId));
      const resolvedContact = contact ?? selectedContactDetail?.data ?? selectedContactDetail;
      if (resolvedContact?.companyId) setCompanyId(Number(resolvedContact.companyId));
    }
    if (dealId) {
      const deal = deals.find((item: any) => Number(item.id) === Number(dealId));
      const resolvedDeal = deal ?? selectedDealDetail?.data ?? selectedDealDetail;
      if (resolvedDeal?.companyId && !companyId) setCompanyId(Number(resolvedDeal.companyId));
      if (resolvedDeal?.contactId && !contactId) setContactId(Number(resolvedDeal.contactId));
    }
  }, [contactId, companyId, dealId, contacts, deals, selectedContactDetail, selectedDealDetail]);

  // Un meeting nato da una scheda contatto deve sempre rappresentare il
  // referente principale anche prima che l'utente aggiunga altri invitati.
  useEffect(() => {
    if (!isOpen || !contactId) return;
    const primary = makeContactParticipant(contactId, "primary", selectedContactDetail);
    if (!primary) return;
    setParticipants((current) => current.some((participant) => participant.contactId === contactId && participant.role === "primary")
      ? current
      : [primary, ...current.filter((participant) => participant.role !== "primary")]);
  }, [isOpen, contactId, contacts, companyContacts, selectedContactDetail]);

  // Build contact options with priority grouping: linked first, then others
  const contactOptions: AutocompleteOption[] = useMemo(() => {
    const linkedIds = new Set(companyContacts.map((c: any) => c.id?.toString()));
    const linkedOptions: AutocompleteOption[] = companyContacts.map((c: any) => ({
      value: c.id.toString(),
      label: `${c.firstName} ${c.lastName}`,
      icon: <User className="h-4 w-4" />,
      highlighted: true,
    }));
    const otherOptions: AutocompleteOption[] = contacts
      .filter((c: any) => !linkedIds.has(c.id?.toString()))
      .map((c: any) => ({
        value: c.id.toString(),
        label: `${c.firstName} ${c.lastName}`,
        icon: <User className="h-4 w-4" />,
      }));
    const result: AutocompleteOption[] = [];
    if (linkedOptions.length > 0) {
      result.push({ value: '_header_linked', label: '', group: 'Contatti dell\'azienda', groupHeader: true });
      result.push(...linkedOptions);
      if (otherOptions.length > 0) {
        result.push({ value: '_header_others', label: '', group: 'Altri contatti', groupHeader: true });
      }
    }
    result.push(...otherOptions);
    return result;
  }, [contacts, companyContacts]);

  // Il calendario passa un draft con id 0 per precompilare Nuovo Evento:
  // e' una creazione, non un PATCH verso un record inesistente.
  const isEditMode = Number(initialData?.id) > 0;
  const invitableParticipantCount = participants.filter((participant) => Boolean(participant.email?.trim())).length;
  const removedInvitedEmails = useMemo(() => {
    if (!isEditMode) return [];
    const currentEmails = new Set(participants
      .map((participant) => participant.email?.trim().toLocaleLowerCase('it-IT'))
      .filter((email): email is string => Boolean(email)));
    const removed = new Map<string, string>();
    for (const participant of Array.isArray(initialData?.participants) ? initialData.participants : []) {
      const email = typeof participant?.email === 'string' ? participant.email.trim() : '';
      if (participant?.invitedAt && email && !currentEmails.has(email.toLocaleLowerCase('it-IT'))) {
        removed.set(email.toLocaleLowerCase('it-IT'), email);
      }
    }
    return [...removed.values()];
  }, [initialData?.participants, isEditMode, participants]);
  const notificationRecipientCount = invitableParticipantCount + removedInvitedEmails.length;

  useEffect(() => {
    if (notificationRecipientCount === 0 && inviteDeliveryIntent === "all") {
      setInviteDeliveryIntent("none");
    }
  }, [notificationRecipientCount, inviteDeliveryIntent]);

  const events: any[] = (Array.isArray(rawEvents) ? rawEvents : (rawEvents?.data ?? []))
    .filter(isMeetingParentEventCandidate)
    .filter((event: any) => Number(event.id) !== Number(initialData?.id));
  const compatibleEvents = events.filter((event) => meetingEventOverlapsLocalDate(event, date));
  const eventOptions: AutocompleteOption[] = [
    { value: 'none', label: 'Nessun evento collegato' },
    ...[...compatibleEvents, ...events.filter((event) => !meetingEventOverlapsLocalDate(event, date))]
      .map((event) => ({
        value: String(event.id),
        label: `${event.title} · ${format(new Date(event.startTime), 'dd/MM/yyyy')} – ${format(new Date(event.endTime), 'dd/MM/yyyy')}`,
        highlighted: meetingEventOverlapsLocalDate(event, date),
      })),
  ];
  const selectParentEvent = (value: string) => {
    setUserEdited(true);
    const nextId = value === 'none' ? null : Number(value);
    const next = events.find((event) => Number(event.id) === nextId);
    if (!dealId || dealId === eventInheritedDealId) {
      const inherited = next?.dealId ? Number(next.dealId) : null;
      setDealId(inherited);
      setEventInheritedDealId(inherited);
    }
    setEventId(nextId);
  };

  // Il Deal e' la fonte primaria delle Sinergie: il partecipante puo' non
  // appartenere direttamente all'azienda o al contatto dell'appuntamento.
  const synergies = allSynergies.filter((syn) => {
    if (dealId) return Number(syn.dealId) === dealId;
    if (!contactId && !companyId) return true;
    const matchContact = contactId ? syn.entityType === 'contact' && syn.entityId === contactId : false;
    const matchCompany = companyId ? syn.entityType === 'company' && syn.entityId === companyId : false;
    return matchContact || matchCompany;
  });

  useEffect(() => {
    if (!dealId || isEditMode) return;
    if (synergyId && !synergies.some((syn) => Number(syn.id) === synergyId)) {
      setSynergyId(null);
      return;
    }
    if (!synergyId && synergies.length === 1) {
      setSynergyId(Number(synergies[0].id));
    }
  }, [dealId, synergies, synergyId, isEditMode]);

  const companyOptions: AutocompleteOption[] = companies.map((c) => ({
    value: c.id.toString(),
    label: c.name,
    icon: <Building2 className="h-4 w-4" />,
  }));

  // Il contesto azienda/contatto ordina i suggerimenti, ma non esclude i Deal:
  // un'opportunita' puo' essere collegata alla persona, alla mandante o a un
  // altro partecipante senza avere questa azienda come companyId principale.
  const dealOptions: AutocompleteOption[] = [...deals]
    .sort((a, b) => {
      const relevance = (deal: any) => Number(contactId != null && Number(deal.contactId) === contactId) * 2
        + Number(companyId != null && (Number(deal.companyId) === companyId || Number(deal.mandanteCompanyId) === companyId));
      return relevance(b) - relevance(a);
    })
    .map((d) => ({
    value: d.id.toString(),
    label: d.name,
    icon: <Target className="h-4 w-4" />,
  }));

  const synergyOptions: AutocompleteOption[] = [
    { value: "", label: "Nessuna sinergia", icon: <Link2 className="h-4 w-4" /> },
    ...synergies.map((syn) => ({
      value: syn.id.toString(),
      label: [syn.dealName, syn.entityName].filter(Boolean).join(' — ') || `Sinergia #${syn.id}`,
      icon: <Link2 className="h-4 w-4" />,
    })),
  ];

  const sendInviteMutation = useMutation({
    mutationFn: async () => {
      if (!pendingInvitePreview) throw new Error("Anteprima invito non disponibile");
      return apiRequest(`/api/meetings/${pendingInvitePreview.meetingId}/send-invite`, {
        method: "POST",
        body: JSON.stringify({ previewToken: pendingInvitePreview.previewToken }),
      });
    },
    onSuccess: (result: any) => {
      const invited = Array.isArray(result.recipients) ? result.recipients : [];
      const cancelled = Array.isArray(result.removedRecipients) ? result.removedRecipients : [];
      toast({
        title: cancelled.length > 0 ? "Aggiornamenti inviati" : "Inviti inviati",
        description: [
          invited.length > 0 ? `Inviti/aggiornamenti: ${invited.join(", ")}` : null,
          cancelled.length > 0 ? `Annullamenti: ${cancelled.join(", ")}` : null,
        ].filter(Boolean).join(" · "),
      });
      if (pendingInvitePreview?.meetingId) invalidateMeetingViews(queryClient, pendingInvitePreview.meetingId);
      setPendingInvitePreview(null);
      closeAfterSave();
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Inviti non inviati", description: error.message, variant: "destructive" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const { inviteDeliveryIntent: _inviteDeliveryIntent, ...meetingPayload } = data;
      return await apiRequest(
        isEditMode ? 'PATCH' : 'POST',
        isEditMode ? `/api/meetings/${initialData!.id}` : '/api/meetings',
        meetingPayload
      );
    },
    onSuccess: async (returnedMeeting, data) => {
      // 1. Aggiornamento SINCRONO della cache generale per apparizione istantanea
      const existingMeetings = queryClient.getQueryData<any[]>(['/api/meetings']) || [];
      if (isEditMode) {
        queryClient.setQueryData(['/api/meetings'], existingMeetings.map(m => m.id === returnedMeeting.id ? returnedMeeting : m));
      } else {
        queryClient.setQueryData(['/api/meetings'], [returnedMeeting, ...existingMeetings]);
      }

      // 2. Aggiornamento SINCRONO della cache per azienda
      if (data.companyId || returnedMeeting.companyId) {
        const cid = data.companyId || returnedMeeting.companyId;
        const companyMeetings = queryClient.getQueryData<any[]>(['/api/meetings/company', cid]) || [];
        const companyRecordKey = ['/api/meetings', { companyId: cid }];
        const companyRecordMeetings = queryClient.getQueryData<any[]>(companyRecordKey) || [];
        if (isEditMode) {
          queryClient.setQueryData(['/api/meetings/company', cid], companyMeetings.map(m => m.id === returnedMeeting.id ? returnedMeeting : m));
          queryClient.setQueryData(companyRecordKey, companyRecordMeetings.map(m => m.id === returnedMeeting.id ? returnedMeeting : m));
        } else {
          queryClient.setQueryData(['/api/meetings/company', cid], [returnedMeeting, ...companyMeetings]);
          queryClient.setQueryData(companyRecordKey, [returnedMeeting, ...companyRecordMeetings]);
        }
        queryClient.invalidateQueries({ queryKey: companyRecordKey });
      }

      // 3. Aggiornamento SINCRONO della cache per contatto
      if (data.contactId || returnedMeeting.contactId) {
        const cid = data.contactId || returnedMeeting.contactId;
        const contactMeetings = queryClient.getQueryData<any[]>(['/api/meetings', { contactId: cid }]) || [];
        if (isEditMode) {
          queryClient.setQueryData(['/api/meetings', { contactId: cid }], contactMeetings.map(m => m.id === returnedMeeting.id ? returnedMeeting : m));
        } else {
          queryClient.setQueryData(['/api/meetings', { contactId: cid }], [returnedMeeting, ...contactMeetings]);
        }
      }

      // Meeting della Filiale: cache distinta dalla societa madre.
      if (data.branchId || returnedMeeting.branchId) {
        const bid = data.branchId || returnedMeeting.branchId;
        const branchKey = ['/api/meetings', { branchId: bid }];
        const branchMeetings = queryClient.getQueryData<any[]>(branchKey) || [];
        queryClient.setQueryData(branchKey, isEditMode
          ? branchMeetings.map(m => m.id === returnedMeeting.id ? returnedMeeting : m)
          : [returnedMeeting, ...branchMeetings]);
        queryClient.invalidateQueries({ queryKey: branchKey });
      }

      // 4. Invalidazione asincrona per sincronizzazione server in background
      // Qualunque superficie che elenca appuntamenti (scheda, dashboard,
      // calendario) deve vedere subito il readback dello stesso meeting.
      queryClient.invalidateQueries({ queryKey: ['/api/meetings'] });
      invalidateMeetingViews(queryClient, returnedMeeting.id);
      if (data.companyId) queryClient.invalidateQueries({ queryKey: ['/api/meetings/company', data.companyId] });
      if (data.branchId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { branchId: data.branchId }] });
      if (data.contactId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { contactId: data.contactId }] });
      if (companyId) queryClient.invalidateQueries({ queryKey: ['/api/meetings/company', companyId] });
      if (branchId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { branchId }] });
      if (contactId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { contactId }] });
      const relatedContactIds = new Set<number>([
        Number(data.contactId || returnedMeeting.contactId),
        ...(Array.isArray(data.participants) ? data.participants.map((participant: any) => Number(participant?.contactId)) : []),
        ...(Array.isArray(returnedMeeting.participants) ? returnedMeeting.participants.map((participant: any) => Number(participant?.contactId)) : []),
      ].filter((id) => Number.isSafeInteger(id) && id > 0));
      const relatedCompanyIds = new Set<number>([
        Number(data.companyId || returnedMeeting.companyId),
        ...(Array.isArray(data.participants) ? data.participants.map((participant: any) => Number(participant?.companyId)) : []),
        ...(Array.isArray(returnedMeeting.participants) ? returnedMeeting.participants.map((participant: any) => Number(participant?.companyId)) : []),
      ].filter((id) => Number.isSafeInteger(id) && id > 0));
      for (const relatedContactId of relatedContactIds) {
        queryClient.invalidateQueries({ queryKey: ['/api/meetings', { contactId: relatedContactId }] });
      }
      for (const relatedCompanyId of relatedCompanyIds) {
        queryClient.invalidateQueries({ queryKey: ['/api/meetings', { companyId: relatedCompanyId }] });
        queryClient.invalidateQueries({ queryKey: ['/api/meetings/company', relatedCompanyId] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });
      // Il Calendario usa useCrmQuery, non la cache /api/meetings. Attendi il
      // readback attivo prima di chiudere la modale, così non resta visibile
      // l'orario precedente dopo un salvataggio riuscito.
      await queryClient.refetchQueries({ queryKey: ['crm-query', 'execute'], type: 'active' });

      toast({
        title: isEditMode ? "Meeting aggiornato" : "Meeting creato",
        description: returnedMeeting.googleCalendarSync?.scheduled
          ? "Salvato nel CRM. La sincronizzazione con Google Calendar è in corso: verifica lo stato nel dettaglio dell'impegno."
          : "Salvato nel CRM. Nessuna sincronizzazione Google avviata.",
        // Michele 20/08: dopo la conferma serve un link diretto per aprire e
        // modificare/aggiungere elementi (es. un appuntamento dentro un
        // Evento appena creato), non solo un avviso testuale.
        action: (
          <ToastAction altText="Apri in calendario" onClick={() => navigate(`/meetings/${returnedMeeting.id}`)}>
            Apri
          </ToastAction>
        ),
      });
      if (!isEditMode && date && onMeetingCreated) {
        onMeetingCreated(new Date(`${date}T${startTime}`), returnedMeeting.id, returnedMeeting);
      }
      if (data.inviteDeliveryIntent === "all") {
        try {
          let preview = await apiRequest(`/api/meetings/${returnedMeeting.id}/invite-preview`);
          if (
            preview?.channel === "google"
            && typeof preview?.blockedReason === "string"
            && preview.blockedReason.includes("sincronizzalo prima")
          ) {
            await apiRequest("POST", `/api/google-calendar/sync-meeting/${returnedMeeting.id}`);
            preview = await apiRequest(`/api/meetings/${returnedMeeting.id}/invite-preview`);
          }
          setPendingInvitePreview({ ...preview, meetingId: returnedMeeting.id });
          setUserEdited(false);
          return;
        } catch (error: any) {
          toast({
            title: "Meeting salvato, anteprima inviti non disponibile",
            description: error.message,
            variant: "destructive",
          });
        }
      }
      closeAfterSave();
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Errore", description: error.message, variant: "destructive" });
    },
  });


  const deleteMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('DELETE', `/api/meetings/${initialData!.id}`);
    },
    onSuccess: () => {
      const deletedId = initialData!.id;
      // Rimozione SINCRONA dalla cache generale
      const existingMeetings = queryClient.getQueryData<any[]>(['/api/meetings']) || [];
      queryClient.setQueryData(['/api/meetings'], existingMeetings.filter(m => m.id !== deletedId));

      // Rimozione SINCRONA dalla cache azienda
      if (companyId) {
        const companyMeetings = queryClient.getQueryData<any[]>(['/api/meetings/company', companyId]) || [];
        queryClient.setQueryData(['/api/meetings/company', companyId], companyMeetings.filter(m => m.id !== deletedId));
      }
      // Rimozione SINCRONA dalla cache contatto
      if (contactId) {
        const contactMeetings = queryClient.getQueryData<any[]>(['/api/meetings', { contactId }]) || [];
        queryClient.setQueryData(['/api/meetings', { contactId }], contactMeetings.filter(m => m.id !== deletedId));
      }
      if (branchId) {
        const branchKey = ['/api/meetings', { branchId }];
        const branchMeetings = queryClient.getQueryData<any[]>(branchKey) || [];
        queryClient.setQueryData(branchKey, branchMeetings.filter(m => m.id !== deletedId));
      }

      // Invalidazione asincrona per sincronizzazione
      invalidateMeetingViews(queryClient, deletedId);
      if (companyId) queryClient.invalidateQueries({ queryKey: ['/api/meetings/company', companyId] });
      if (branchId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { branchId }] });
      if (contactId) queryClient.invalidateQueries({ queryKey: ['/api/meetings', { contactId }] });
      for (const participant of Array.isArray(initialData?.participants) ? initialData.participants : []) {
        const participantContactId = Number(participant?.contactId);
        const participantCompanyId = Number(participant?.companyId);
        if (Number.isSafeInteger(participantContactId) && participantContactId > 0) {
          queryClient.invalidateQueries({ queryKey: ['/api/meetings', { contactId: participantContactId }] });
        }
        if (Number.isSafeInteger(participantCompanyId) && participantCompanyId > 0) {
          queryClient.invalidateQueries({ queryKey: ['/api/meetings', { companyId: participantCompanyId }] });
          queryClient.invalidateQueries({ queryKey: ['/api/meetings/company', participantCompanyId] });
        }
      }
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard'] });

      toast({ title: "Meeting eliminato" });
      closeAfterSave();
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Errore eliminazione", description: error.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setLocation(initialData.location || "");
      setMeetingType(initialData.meetingType || "Call");
      const existingStart = initialData.startTime ? new Date(initialData.startTime) : null;
      const existingEnd = initialData.endTime ? new Date(initialData.endTime) : null;
      // Un orario gia salvato può venire dalla fonte oppure da Michele. In
      // entrambi i casi non lo azzeriamo durante un edit successivo: la
      // distinzione di evidenza limita l'automazione, non la correzione umana.
      setEventHasTimes(initialData.meetingType === 'Evento'
        && Boolean(existingStart) && Boolean(existingEnd)
        && !(format(existingStart!, 'HH:mm') === '00:00' && format(existingEnd!, 'HH:mm') === '23:59'));
      setCalendarScope(initialData.calendarScope === "private" ? "private" : "work");
      setContactId(initialData.contactId ?? propContactId ?? null);
      setCompanyId(initialData.companyId ?? propCompanyId ?? null);
      setBranchId(initialData.branchId ?? propBranchId ?? null);
      setDealId(initialData.dealId ?? propDealId ?? null);
      setLeadId(initialData.leadId ?? propLeadId ?? null);
      setSynergyId(initialData.synergyId ?? null);
      setEventId(initialData.eventId ?? null);
      setEventInheritedDealId(null);
      const canonicalParticipants = Array.isArray(initialData.participants) && initialData.participants.length > 0
        ? initialData.participants
        : legacyMeetingParticipantDrafts(initialData.attendees);
      setParticipants(canonicalParticipants.length > 0
        ? canonicalParticipants.map((participant: any) => ({
            contactId: participant.contactId ? Number(participant.contactId) : null,
            companyId: participant.companyId ? Number(participant.companyId) : null,
            email: participant.email || null,
            displayName: participant.displayName || participant.contactName || null,
            role: participant.role || "attendee",
            isOptional: participant.isOptional === true,
            invitedAt: participant.invitedAt || null,
            inviteChannel: participant.inviteChannel || null,
          }))
        : []);

      if (initialData.startTime) {
        const d = new Date(initialData.startTime);
        setDate(format(d, "yyyy-MM-dd"));
        setStartTime(format(d, "HH:mm"));
      }
      if (initialData.endTime) {
        const endD = new Date(initialData.endTime);
        setEndDate(format(endD, "yyyy-MM-dd"));
        setEndTime(format(endD, "HH:mm"));
      }
    } else if (defaultDate) {
      setDate(format(defaultDate, "yyyy-MM-dd"));
      setEndDate(format(defaultDate, "yyyy-MM-dd"));
      setStartTime("09:00");
      setEndTime("10:00");
      setContactId(propContactId ?? null);
      setCompanyId(propCompanyId ?? null);
      setBranchId(propBranchId ?? null);
      setDealId(propDealId ?? null);
      setLeadId(propLeadId ?? null);
      setEventId(null);
      setEventInheritedDealId(null);
      setParticipants([]);
      setCalendarScope("work");
      setEventHasTimes(false);
    }
    setShowDeleteConfirm(false);
    setInviteDeliveryIntent("none");
    setPendingInvitePreview(null);
    setUserEdited(false);
  }, [initialData, defaultDate, isOpen, propCompanyId, propBranchId, propContactId, propDealId, propLeadId]);

  const resetForm = () => {
    setTitle(""); setDescription(""); setLocation("");
    setMeetingType("Call"); setCalendarScope("work"); setContactId(null); setCompanyId(null); setBranchId(propBranchId ?? null); setDealId(null); setLeadId(null); setSynergyId(null);
    setParticipants([]); setParticipantPickerValue(""); setExternalParticipantEmail("");
    setDate(""); setEndDate(""); setStartTime("09:00"); setEndTime("10:00"); setEventHasTimes(false);
    setEventId(null); setEventInheritedDealId(null);
    setShowDeleteConfirm(false);
    setSyncWithGoogle(true);
    setInviteDeliveryIntent("none");
    setPendingInvitePreview(null);
    setUserEdited(false);
  };

  const discardAndClose = () => {
    if (requestClose(false)) resetForm();
  };

  const [loadingParticipantId, setLoadingParticipantId] = useState<number | null>(null);

  const resolveContactForParticipant = async (id: number) => {
    const cached = normalizeContactRecord(contactById(id));
    if (cached) return cached;
    try {
      const payload = await apiRequest(`/api/contacts/${id}`);
      return normalizeContactRecord(payload);
    } catch {
      toast({ title: "Contatto non disponibile", description: "Non ho potuto leggere l'azienda collegata al contatto.", variant: "destructive" });
      return null;
    }
  };

  const selectPrimaryContact = async (value: string) => {
    setUserEdited(true);
    const id = value ? parseInt(value, 10) : null;
    setContactId(id);
    setParticipantPickerValue("");
    setParticipants((current) => {
      const withoutPrimary = current.filter((participant) => participant.role !== "primary" && participant.contactId !== id);
      const primary = id ? makeContactParticipant(id, "primary") : null;
      return primary ? [primary, ...withoutPrimary] : withoutPrimary;
    });
    if (id && !contactById(id)) {
      setLoadingParticipantId(id);
      const contact = await resolveContactForParticipant(id);
      setLoadingParticipantId(null);
      const primary = contact ? makeContactParticipant(id, "primary", contact) : null;
      if (primary) {
        setParticipants((current) => [primary, ...current.filter((participant) => participant.role !== "primary" && participant.contactId !== id)]);
      }
    }
  };

  const addParticipantContact = async (value: string) => {
    const id = value ? parseInt(value, 10) : null;
    setParticipantPickerValue("");
    if (!id || id === contactId) return;
    setUserEdited(true);
    const contact = await resolveContactForParticipant(id);
    setLoadingParticipantId(null);
    const participant = contact ? makeContactParticipant(id, "attendee", contact) : null;
    if (!participant) return;
    setParticipants((current) => current.some((item) => item.contactId === id) ? current : [...current, participant]);
  };

  const addExternalParticipant = () => {
    const email = externalParticipantEmail.trim();
    if (!email || !email.includes("@")) return;
    setUserEdited(true);
    setParticipants((current) => current.some((item) => item.email?.toLowerCase() === email.toLowerCase())
      ? current
      : [...current, { contactId: null, companyId: null, email, displayName: null, role: "attendee", isOptional: false }]);
    setExternalParticipantEmail("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast({ title: "Errore", description: "Il titolo è obbligatorio.", variant: "destructive" });
      return;
    }
    if (!date) {
      toast({ title: "Errore", description: "La data è obbligatoria.", variant: "destructive" });
      return;
    }

    const isEvento = meetingType === 'Evento';
    // La fonte può proporre un Evento senza orari; Michele può però decidere
    // esplicitamente di programmare orari. La mancanza di evidenza blocca solo
    // l'automazione, mai una correzione/manuale dell'utente.
    const preservesTimedEvento = isEvento && eventHasTimes;
    let startDateTime: Date;
    let endDateTime: Date;

    if (isEvento) {
      const resolvedEndDate = endDate || date;
      if (resolvedEndDate < date) {
        toast({ title: "Errore", description: "La data di fine deve essere successiva o uguale all'inizio.", variant: "destructive" });
        return;
      }
      if (preservesTimedEvento) {
        startDateTime = new Date(`${date}T${startTime}`);
        endDateTime = new Date(`${resolvedEndDate}T${endTime}`);
        if (endDateTime <= startDateTime) {
          toast({ title: "Errore", description: "L'ora di fine deve essere successiva all'inizio.", variant: "destructive" });
          return;
        }
      } else {
        // Un nuovo Evento occupa giorni interi: blocca la giornata dal suo
        // inizio alla sua fine (00:00 -> 23:59:59 locale).
        startDateTime = new Date(`${date}T00:00:00`);
        endDateTime = new Date(`${resolvedEndDate}T23:59:59`);
      }
    } else {
      startDateTime = new Date(`${date}T${startTime}`);
      endDateTime = new Date(`${date}T${endTime}`);
      if (endDateTime <= startDateTime) {
        toast({ title: "Errore", description: "L'ora di fine deve essere successiva all'inizio.", variant: "destructive" });
        return;
      }
    }

    saveMutation.mutate({
      title: title.trim(),
      description: description.trim() || null,
      location: location.trim() || null,
      meetingType,
      calendarScope,
      contactId: contactId || null,
      companyId: companyId || null,
      branchId: branchId || null,
      dealId: dealId || null,
      leadId: leadId || null,
      synergyId: synergyId || null,
      eventId: isEvento ? null : (eventId || null),
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      status: "scheduled",
      syncWithGoogle: inviteDeliveryIntent === "all" && inviteChannel?.channel === "google"
        ? false
        : syncWithGoogle,
      participants,
      inviteDeliveryIntent,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="meeting-modal-title" className="max-h-[92dvh] w-full max-w-xl overflow-x-hidden overflow-y-auto border border-border bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-4 py-3 sm:px-5 sm:py-4">
          <h2 id="meeting-modal-title" className="text-base font-semibold text-foreground">
            {isEditMode
              ? (meetingType === 'Evento' ? 'Modifica Evento' : 'Modifica Appuntamento')
              : (meetingType === 'Evento' ? 'Nuovo Evento' : 'Nuovo Appuntamento')}
          </h2>
          <Button variant="ghost" size="icon" onClick={discardAndClose} className="h-7 w-7">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {pendingInvitePreview && (
          <div className="space-y-4 p-4 sm:p-5" aria-label="Anteprima inviti">
            <div className="border border-primary/25 bg-primary/5 p-4">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 space-y-2">
                  <p className="text-sm font-semibold">Conferma invio a tutti</p>
                  <p className="text-xs text-muted-foreground">
                    Meeting salvato. Controlla l’anteprima prima dell’invio effettivo.
                  </p>
                  <div className="space-y-1 text-xs">
                    <p><span className="font-medium">Da:</span> {pendingInvitePreview.from || "Non disponibile"}</p>
                    <p className="break-words"><span className="font-medium">A:</span> {pendingInvitePreview.recipients.join(", ") || "Nessun destinatario"}</p>
                    {pendingInvitePreview.removedRecipients.length > 0 && (
                      <p className="break-words"><span className="font-medium">Annullamento a:</span> {pendingInvitePreview.removedRecipients.join(", ")}</p>
                    )}
                    <p className="break-words"><span className="font-medium">Oggetto:</span> {pendingInvitePreview.subject}</p>
                    <p><span className="font-medium">Canale:</span> {pendingInvitePreview.channel === "google" ? "Google Calendar" : "Email CRM"}</p>
                  </div>
                </div>
              </div>
            </div>
            {pendingInvitePreview.blockedReason && (
              <p className="border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                {pendingInvitePreview.blockedReason}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => { setPendingInvitePreview(null); closeAfterSave(); resetForm(); }}>
                Chiudi senza inviare
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => sendInviteMutation.mutate()}
                disabled={Boolean(pendingInvitePreview.blockedReason) || sendInviteMutation.isPending}
              >
                {sendInviteMutation.isPending ? "Invio..." : "Conferma invio a tutti"}
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} onChangeCapture={() => setUserEdited(true)} className={cn("space-y-4 p-3 sm:p-5", pendingInvitePreview && "hidden")}>
          {isEditMode && initialData?.id && (
            <MartinaOutputRating
              outputType="meeting"
              outputId={initialData.id}
              label="Quanto e' importante questo appuntamento?"
              context={{ surface: 'meeting_edit' }}
            />
          )}

          {/* Titolo */}
          <div className="space-y-1">
            <Label htmlFor="title" className="text-xs font-medium">Titolo *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Inserisci il titolo del meeting..."
              className="h-9"
              required
            />
          </div>

          {/* Tipo meeting */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Tipo appuntamento</Label>
            <div className="flex gap-2">
              {([['work', 'Lavoro'], ['private', 'Privato']] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setUserEdited(true); setCalendarScope(value); }}
                  className={cn(
                    "flex-1 border py-1.5 text-xs transition-colors",
                    calendarScope === value
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border text-muted-foreground hover:bg-muted"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tipo meeting */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Tipo</Label>
            <div className="flex gap-2">
              {MEETING_TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setUserEdited(true); setMeetingType(value); }}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs border transition-colors",
                    meetingType === value
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border text-muted-foreground hover:bg-muted"
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Data e orari */}
          {meetingType === 'Evento' ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="date" className="text-xs font-medium">Dal *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    const nextDate = e.target.value;
                    setDate(nextDate);
                    // "Al" mostrava ancora la data precedente (es. oggi, dal
                    // default dell'evento creato dall'email): il "min" sul
                    // campo sotto guida solo il selettore, non corregge un
                    // valore gia' impostato. Senza questo allineamento si
                    // poteva creare un Evento con fine prima dell'inizio.
                    if (endDate && endDate < nextDate) setEndDate(nextDate);
                  }}
                  className="h-9"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eventEndDate" className="text-xs font-medium">Al</Label>
                <Input id="eventEndDate" type="date" value={endDate} min={date || undefined} onChange={(e) => setEndDate(e.target.value)} className="h-9" placeholder={date} />
              </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={eventHasTimes} onChange={(event) => setEventHasTimes(event.target.checked)} />
                Questo Evento ha orari specifici
              </label>
              {eventHasTimes && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1"><Label htmlFor="eventStartTime" className="text-xs font-medium">Ora inizio</Label><Input id="eventStartTime" type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} className="h-9" /></div>
                  <div className="space-y-1"><Label htmlFor="eventEndTime" className="text-xs font-medium">Ora fine</Label><Input id="eventEndTime" type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} className="h-9" /></div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Senza orari, l’Evento copre tutti i giorni selezionati. Gli orari qui sono una tua scelta esplicita e non cancellano l’Evento.</p>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="date" className="text-xs font-medium">Data *</Label>
                <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9" required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="startTime" className="text-xs font-medium">Inizio</Label>
                <Input id="startTime" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-9" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="endTime" className="text-xs font-medium">Fine</Label>
                <Input id="endTime" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="h-9" />
              </div>
            </div>
          )}

          {meetingType !== 'Evento' && (
            <div className="min-w-0 space-y-1">
              <Label htmlFor="meeting-parent-event" className="text-xs font-medium">Evento collegato (facoltativo)</Label>
              <Autocomplete id="meeting-parent-event" options={eventOptions} value={eventId ? String(eventId) : 'none'}
                onValueChange={selectParentEvent} searchPlaceholder="Cerca un evento..."
                placeholder={eventId ? initialData?.eventTitle || 'Evento collegato non disponibile' : 'Nessun evento collegato'}
                loading={loadingEvents} errorText={eventsError ? 'Non riesco a caricare gli Eventi. Riapri il pannello per riprovare.' : undefined}
                notFoundText="Nessun evento trovato" />
              <p className="text-xs text-muted-foreground">
                {compatibleEvents.length > 1 ? `${compatibleEvents.length} eventi coprono questa data. Scegli quello pertinente.`
                  : compatibleEvents.length === 1 ? 'L’evento che copre questa data è evidenziato. Puoi sceglierne un altro.'
                    : 'Puoi cercare un evento o lasciare l’appuntamento senza collegamento.'}
              </p>
              {eventId && <Button type="button" size="sm" variant="ghost" className="h-7 px-0 text-xs" onClick={() => selectParentEvent('none')}>Rimuovi collegamento evento</Button>}
            </div>
          )}

          {/* Luogo */}
          <div className="space-y-1">
            <Label htmlFor="location" className="text-xs font-medium">Luogo / Link</Label>
            {meetingType === 'In-Person' ? (
              <AddressAutocomplete
                id="location"
                mode="address"
                value={location}
                onChange={(value) => { setUserEdited(true); setLocation(value); }}
                placeholder="Cerca l'indirizzo del meeting..."
                className="h-9"
              />
            ) : (
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Indirizzo o link meeting..."
                className="h-9"
              />
            )}
          </div>

          {/* Descrizione */}
          <div className="space-y-1">
            <Label htmlFor="description" className="text-xs font-medium">Descrizione</Label>
            <VoiceAssistantTextarea
              id="description"
              value={description}
              onValueChange={(value) => { setUserEdited(true); setDescription(value); }}
              fieldLabel="Descrizione e agenda Meeting"
              martinaContext={meetingMartinaContext}
              placeholder="Aggiungi note o agenda..."
              rows={2}
              className="resize-none"
            />
          </div>

          {/* Collegamento entità — stesso pattern di DealModal */}
          <div className="border border-border p-3 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Collega a</p>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Referente principale</Label>
              <CrmRecordAutocomplete
                entityType="contact"
                initialOptions={contactOptions}
                companyScopeId={companyId}
                value={contactId?.toString() ?? ""}
                onValueChange={selectPrimaryContact}
                placeholder="Cerca contatto..."
                selectedLabel={contactOptions.find((option) => option.value === contactId?.toString())?.label || initialData?.contactName}
                icon={<User className="h-4 w-4" />}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">Partecipanti</Label>
              <CrmRecordAutocomplete
                entityType="contact"
                initialOptions={contactOptions}
                value={participantPickerValue}
                onValueChange={addParticipantContact}
                placeholder="Aggiungi un altro contatto..."
                selectedLabel=""
                excludeIds={participants.map((participant) => participant.contactId).filter((id): id is number => id != null)}
                icon={<Users className="h-4 w-4" />}
              />
              <div className="flex gap-2">
                <Input
                  value={externalParticipantEmail}
                  onChange={(event) => setExternalParticipantEmail(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addExternalParticipant(); } }}
                  placeholder="Oppure email esterna..."
                  className="h-9"
                  type="email"
                />
                <Button type="button" variant="outline" size="sm" onClick={addExternalParticipant} className="h-9 shrink-0" aria-label="Aggiungi partecipante esterno">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {participants.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border border-border p-2" aria-label="Partecipanti selezionati">
                  {participants.map((participant, index) => (
                    <span key={`${participant.contactId ?? participant.email}-${index}`} className="inline-flex max-w-full items-center gap-1 border border-primary/20 bg-primary/10 px-2 py-1 text-xs text-primary">
                      <User className="h-3.5 w-3.5 shrink-0" />
                      <span className="max-w-[18rem] truncate">
                        {participant.displayName || participant.email || "Partecipante"}
                        {(participant.companyName || participant.companyId) ? ` · ${participant.companyName || companies.find((company: any) => Number(company.id) === participant.companyId)?.name || "Azienda collegata"}` : ""}
                      </span>
                      {participant.role === "primary" && <span className="text-[10px] text-muted-foreground">principale</span>}
                      {participant.role !== "primary" && (
                        <button type="button" className="ml-0.5 inline-flex h-5 w-5 items-center justify-center text-destructive hover:bg-destructive/10" onClick={() => { setUserEdited(true); setParticipants((current) => current.filter((_, itemIndex) => itemIndex !== index)); }} aria-label={`Rimuovi ${participant.displayName || participant.email || "partecipante"}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {loadingParticipantId != null && <p className="text-xs text-muted-foreground">Leggo il contatto e la sua azienda…</p>}
              <p className="text-xs text-muted-foreground">Ogni contatto viene aggiunto come tag con la propria azienda collegata automaticamente.</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Azienda</Label>
              <CrmRecordAutocomplete
                entityType="company"
                initialOptions={companyOptions}
                value={companyId?.toString() ?? ""}
                onValueChange={(v) => { setUserEdited(true); setCompanyId(v ? parseInt(v) : null); }}
                placeholder="Cerca azienda..."
                selectedLabel={companyOptions.find((option) => option.value === companyId?.toString())?.label || initialData?.companyName}
                icon={<Building2 className="h-4 w-4" />}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Opportunità</Label>
              <CrmRecordAutocomplete
                entityType="deal"
                initialOptions={dealOptions}
                value={dealId?.toString() ?? ""}
                onValueChange={(v) => { setUserEdited(true); setDealId(v ? parseInt(v) : null); setEventInheritedDealId(null); }}
                placeholder="Cerca opportunità..."
                selectedLabel={dealOptions.find((option) => option.value === dealId?.toString())?.label}
                icon={<Target className="h-4 w-4" />}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Lead</Label>
              <CrmRecordAutocomplete
                entityType="lead"
                value={leadId?.toString() ?? ""}
                onValueChange={(v) => { setUserEdited(true); setLeadId(v ? parseInt(v) : null); }}
                placeholder="Cerca lead da valutare..."
                selectedLabel={
                  selectedLeadDetail
                    ? [selectedLeadDetail.firstName, selectedLeadDetail.lastName].filter(Boolean).join(" ")
                    : initialData?.leadName
                }
                icon={<UserPlus className="h-4 w-4" />}
              />
              {leadId && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Alla conversione del Lead in Contatto, questo appuntamento si collega automaticamente al nuovo Contatto.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium flex items-center gap-1">
                <Link2 className="h-3 w-3" />
                Sinergia
                {isEditMode && synergyId && (
                  <span className="text-xs text-muted-foreground font-normal ml-1">(non modificabile)</span>
                )}
              </Label>
              {isEditMode && synergyId ? (
                <div className="h-9 px-3 flex items-center border border-border bg-muted text-sm text-muted-foreground">
                  {allSynergies.find(s => s.id === synergyId)
                    ? [allSynergies.find(s => s.id === synergyId)?.dealName, allSynergies.find(s => s.id === synergyId)?.entityName].filter(Boolean).join(' — ')
                    : `Sinergia #${synergyId}`}
                </div>
              ) : (
                <Autocomplete
                  options={synergyOptions}
                  value={synergyId?.toString() ?? ""}
                  onValueChange={(v) => { setUserEdited(true); setSynergyId(v ? parseInt(v) : null); }}
                  placeholder="Cerca sinergia..."
                  searchPlaceholder="Cerca per deal o entità..."
                  notFoundText="Nessuna sinergia trovata."
                  disabled={isEditMode && !!synergyId}
                />
              )}
              {dealId && synergies.length === 1 && !isEditMode && (
                <p className="mt-1 text-xs text-muted-foreground">Unica Sinergia del Deal, precompilata automaticamente.</p>
              )}
              {dealId && synergies.length > 1 && !isEditMode && (
                <p className="mt-1 text-xs text-muted-foreground">Seleziona uno dei {synergies.length} partecipanti del Deal.</p>
              )}
            </div>
          </div>

          <fieldset className="space-y-2 border border-primary/20 bg-primary/5 p-3">
            <legend className="px-1 text-xs font-semibold text-foreground">Inviti ai partecipanti</legend>
            <label className="flex cursor-pointer items-start gap-2 text-xs">
              <input
                type="radio"
                name="invite-delivery-intent"
                value="none"
                checked={inviteDeliveryIntent === "none"}
                onChange={() => setInviteDeliveryIntent("none")}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="font-medium text-foreground">Non mandare inviti</span>
                <span className="mt-0.5 block text-muted-foreground">Salva o aggiorna l’appuntamento senza avvisare nessuno.</span>
              </span>
            </label>
            <label className={cn("flex items-start gap-2 text-xs", notificationRecipientCount > 0 ? "cursor-pointer" : "cursor-not-allowed opacity-60")}>
              <input
                type="radio"
                name="invite-delivery-intent"
                value="all"
                checked={inviteDeliveryIntent === "all"}
                onChange={() => setInviteDeliveryIntent("all")}
                disabled={notificationRecipientCount === 0}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="font-medium text-foreground">Manda aggiornamenti a tutti ({notificationRecipientCount})</span>
                <span className="mt-0.5 block text-muted-foreground">
                  Dopo il salvataggio mostrerò mittente, destinatari e oggetto prima della conferma finale.
                  {removedInvitedEmails.length > 0 ? ` Include ${removedInvitedEmails.length} annullament${removedInvitedEmails.length === 1 ? "o" : "i"} per le persone rimosse.` : ""}
                </span>
              </span>
            </label>
          </fieldset>

          {/* Sincronizza con Google Calendar */}
          {syncStatus?.connected && (
            <div className="flex items-start gap-3 border border-border bg-background p-3">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <input
                    id="sync-google"
                    type="checkbox"
                    checked={syncWithGoogle}
                    onChange={(e) => setSyncWithGoogle(e.target.checked)}
                    className="h-4 w-4 rounded-none border-border text-primary focus:ring-primary"
                  />
                  <Label htmlFor="sync-google" className="text-xs font-medium text-foreground cursor-pointer">
                    Sincronizza con Google Calendar
                  </Label>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {syncStatus?.syncEnabled
                    ? "Il meeting verrà creato/aggiornato automaticamente su Google Calendar."
                    : "Attiva il sync nelle impostazioni per sincronizzare automaticamente."}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="sticky bottom-0 z-10 -mx-3 -mb-3 flex items-center justify-between border-t border-border bg-background px-3 py-3 sm:-mx-5 sm:-mb-5 sm:px-5">
            <div>
              {isEditMode && !showDeleteConfirm && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Elimina
                </Button>
              )}
              {showDeleteConfirm && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-destructive">Eliminare?</span>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="h-7 text-xs"
                  >
                    {deleteMutation.isPending ? "..." : "Sì"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="h-7 text-xs"
                  >
                    No
                  </Button>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={discardAndClose} disabled={saveMutation.isPending} className="h-8 text-xs">
                Annulla
              </Button>
              <Button type="submit" size="sm" disabled={saveMutation.isPending} className="h-8 text-xs">
                {saveMutation.isPending
                  ? "Salvataggio..."
                  : inviteDeliveryIntent === "all"
                    ? isEditMode ? "Aggiorna e prepara inviti" : "Crea e prepara inviti"
                  : isEditMode
                    ? "Aggiorna"
                    : meetingType === "Evento" ? "Crea evento" : "Crea appuntamento"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
